import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Autocomplete,
  Avatar,
  Button,
  CssBaseline,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { useLogin } from '../../hooks/useLogin'
import { Box } from '@mui/system'
import { authService } from '../../services/authService'
import { isValidEmail } from '../../utility/validator'

import InputAdornment from '@mui/material/InputAdornment'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import WaveBackground from './components/WaveBackground'
import { useCustomTheme } from '../../context/ThemeContext'
import { Container } from '@mui/material'

import './Signup.css'
import { decryptData, encryptData } from '../../utility/encrypt'

const LAST_LOGGED_IN_COMPANY_HASH = 'last_logged_in_company_hash'

const Login = () => {
  const navigate = useNavigate()
  let { login, isLoading, error, setError } = useLogin()
  const { setTheme } = useCustomTheme()

  const companyIdHash = useParams()?.info

  const [companyId, setCompanyId] = useState(decryptData(companyIdHash))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [validEmail, setValidEmail] = useState(false)
  const [companies, setCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [associationError, setAssociationError] = useState('')
  const [isSticky, setIsSticky] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleClickShowPassword = () => setShowPassword((show) => !show)

  const handleScroll = () => {
    if (window.scrollY > 50) {
      setIsSticky(true)
    } else {
      setIsSticky(false)
    }
  }

  useEffect(() => {
    if (companyIdHash) {
      localStorage.setItem(LAST_LOGGED_IN_COMPANY_HASH, companyIdHash)
      setCompanyId(decryptData(companyIdHash))
    } else {
      const lsCompanyIdHash = localStorage.getItem(LAST_LOGGED_IN_COMPANY_HASH)
      if (lsCompanyIdHash) {
        navigate(`/login/${lsCompanyIdHash}`)
      }
    }
  }, [companyIdHash])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      if (companyId) {
        const companyRes = await authService.getCompanyDetails(companyId)
        if (companyRes.exists) {
          setSelectedCompany(companyRes.company)
          setCompanies([companyRes.company])
          setTheme(
            companyRes.company?.metadata?.primary_color,
            companyRes.company?.metadata?.secondary_color
          )
        }
      }
    })()
  }, [companyId])

  useEffect(() => {
    ;(async () => {
      setError('')
      setAssociationError('')
      if (isValidEmail(email)) {
        const res = await authService.isUserExist(email, companyId)
        setValidEmail(res.exists)

        if (companies?.length > 0 && res.companies?.length <= 0) {
          // don't change the companies list and selected company
          if (companyId) {
            setAssociationError(
              `${email} is not associated with ${selectedCompany?.company_name}. Contact your admin team for help.`
            )
            setValidEmail(false)
          } else {
            setAssociationError('')
          }
        } else {
          setCompanies(res.companies)
          if (res?.companies?.length === 1) {
            const comp = res?.companies[0]
            setSelectedCompany(comp)
            setTheme(comp?.metadata?.primary_color, comp?.metadata?.secondary_color)
          }
          setAssociationError('')
        }
      }
    })()
  }, [email, companyId]) // important, do not include the companies as dependency

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (selectedCompany?.id) {
      await login(email, password, selectedCompany?.id)
      localStorage.setItem(LAST_LOGGED_IN_COMPANY_HASH, encryptData(selectedCompany?.id))
    } else {
      setAssociationError('Company selection is required to continue login!')
    }
  }

  const handleCompanyChange = (v) => {
    setSelectedCompany(v)
    setTheme(v?.metadata?.primary_color, v?.metadata?.secondary_color)
  }

  const loginThroughDifferentCompany = async () => {
    localStorage.removeItem(LAST_LOGGED_IN_COMPANY_HASH)
    setSelectedCompany(null)
    navigate('/login')
  }

  return (
    <Box
      className="landing-page"
      component="form"
      onSubmit={handleSubmit}
      sx={{ mt: 1 }}
      style={{
        padding: 24,
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: '#fdfdfd',
      }}
    >
      <WaveBackground />

      <nav className={`navbar navbar-expand-lg navbar-light  ${isSticky ? 'sticky' : 'notify-bx'}`}>
        <Container maxWidth="lg">
          <div className="navbar-brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/branding/e7_logo_black.png" alt="Epoch7 Logo" style={{ width: '109px' }} />
          </div>
        </Container>
      </nav>

      <CssBaseline />

      <Box
        className="onboardingForm"
        style={{
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          background: ' #fff',
          borderRadius: 12,
          boxShadow: '0 24px 64px #26214a1a',
          padding: '60px 60px',
          width: 500,
          zIndex: 1,
        }}
      >
        <Typography
          variant="h5"
          color="textSecondary"
          style={{
            color: '#27104E',
            fontSize: '21px',
            fontWeight: '800',
            marginBottom: selectedCompany ? 16 : 32,
          }}
        >
          Log In to Your Account
        </Typography>
        {selectedCompany && (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 24,
              flexDirection: 'column',
            }}
          >
            <Avatar
              alt={selectedCompany?.company_name}
              src={`data:image/png;base64,${selectedCompany?.metadata?.logoBase64}`}
              sx={{ width: 64, height: 64 }}
            />
            <Typography variant="p" fontSize={12} fontWeight="bold">
              {selectedCompany?.company_name}
            </Typography>
          </Box>
        )}
        <TextField
          type="email"
          onChange={(e) => setEmail(e.target.value)}
          label="Email Address"
          variant="outlined"
          color="primary"
          fullWidth
          inputProps={{ style: { fontSize: '16px' } }}
          value={email}
          size="small"
          style={{ borderColor: '#D6D9DE', borderRadius: '10px' }}
          className="inputsout"
        />
        {companies?.length > 0 && !selectedCompany ? (
          <Autocomplete
            disablePortal
            options={companies}
            getOptionLabel={(it) => it.company_name}
            variant="outlined"
            color="primary"
            fullWidth
            value={companies?.find((it) => it?.id == selectedCompany?.id)}
            size="small"
            className="inputsout"
            onChange={(_, v) => handleCompanyChange(v)}
            sx={{
              '& .MuiAutocomplete-endAdornment': {
                marginTop: '-6px',
              },
            }}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  label="Select Company"
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '40px',
                      lineHeight: '40px',
                      paddingRight: '30px',
                      padding: '0px',
                    },
                  }}
                />
              )
            }}
          />
        ) : null}

        <TextField
          onChange={(e) => setPassword(e.target.value)}
          label="Password"
          type={showPassword ? 'text' : 'password'}
          variant="outlined"
          fullWidth
          inputProps={{ style: { fontSize: '16px' } }}
          style={{ borderColor: '#D6D9DE', borderRadius: '10px' }}
          className="inputsout"
          color="primary"
          value={password}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {(error || associationError) && (
          <Typography
            margin="dense"
            variant="caption"
            color="text.error"
            textAlign="center"
            style={{ marginBottom: 12 }}
          >
            {error == 'Email not found.'
              ? `${email} is not associated with ${selectedCompany?.company_name}. Contact your admin team for help.`
              : error || associationError}
          </Typography>
        )}

        <Button
          fullWidth
          disabled={isLoading || !validEmail}
          color="primary"
          variant="contained"
          type="submit"
          className="onboarding-btn"
          onClick={handleSubmit}
        >
          Log In
        </Button>

        {companies?.length > 1 && selectedCompany != null && (
          <Typography
            style={{ marginTop: 24, fontSize: 14, cursor: 'pointer' }}
            color="primary"
            onClick={loginThroughDifferentCompany}
          >
            Login With Another Company Account
          </Typography>
        )}

        <Typography
          style={{ marginTop: 16, fontSize: 14, cursor: 'pointer' }}
          color="primary"
          onClick={() => navigate('/reset-password')}
        >
          Reset Password
        </Typography>
      </Box>
    </Box>
  )
}

export default Login
